"use client";

/**
 * Inline editable blanks in a sentence.
 *
 * Extracted from `compass-step1.jsx:289-294` (`.cs-blankline`, `.blank`, `.blank.empty`,
 * `.caret` classes).
 */

export interface FillInBlankSegment {
  /** Whether this segment is static text or an editable blank. */
  type: "text" | "blank";
  /** Text content (static text or filled-in value). */
  value?: string;
  /** Whether the blank has been filled in. */
  filled?: boolean;
  /** Whether the blank is currently focused / active. */
  active?: boolean;
}

export interface FillInBlankProps {
  /** Ordered segments that compose the sentence. */
  segments: FillInBlankSegment[];
}

/**
 * Sentence with inline blanks that can be filled in.
 *
 * `.cs-blankline` → flex wrap, items-baseline, gap
 * `.blank`        → underline, editable, styled when empty
 */
export function FillInBlank({ segments }: FillInBlankProps) {
  return (
    <div className="flex flex-wrap items-baseline gap-[3px] text-[15px] leading-[1.6] text-(--fg-primary)">
      {segments.map((seg, i) => {
        if (seg.type === "text") {
          return (
            <span key={i} className="whitespace-pre-wrap">
              {seg.value}
            </span>
          );
        }

        const isEmpty = !seg.filled && !seg.value;
        return (
          <span
            key={i}
            className={[
              "inline-flex items-center",
              "border-b-2 px-[6px] py-[2px]",
              "min-w-[80px]",
              "font-semibold",
              "transition-colors duration-150",
              isEmpty
                ? "border-dashed border-(--border-default) text-(--fg-muted)"
                : "border-(--fg-brand) text-(--fg-primary)",
              seg.active ? "border-(--fg-brand)" : "",
            ].join(" ")}
          >
            {seg.value || "\u00A0"}
            {seg.active && (
              <span className="inline-block w-[2px] h-[16px] bg-(--brand-navy) ml-[2px] animate-[blink_1s_steps(2)_infinite]" />
            )}
          </span>
        );
      })}
    </div>
  );
}
